"use client";
import { useState } from "react";
import { postJson } from "@/lib/api";
import Link from "next/link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import InlineBanner from "@/components/ui/inline-banner";
import { Loader2 } from "lucide-react";

export default function ForgotPage() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setErr(null);
    // basic validation
    if (!email.trim()) {
      setErr("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      await postJson("/api/auth/forgot", { email });
      setMsg("If your email is registered, a reset link has been sent.");
    } catch (e: any) {
      setErr(e?.message || "Request failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[calc(100vh-140px)] flex items-center justify-center px-4">
      <Card className="w-full max-w-md rounded-2xl border border-lime-400 dark:border-zinc-700 auth-panel text-zinc-900 dark:text-zinc-100">
        <CardHeader className="pb-2 text-center">
          <CardTitle className="text-2xl text-zinc-900 dark:text-zinc-100">Forgot password</CardTitle>
          <CardDescription className="text-sm mt-1 text-zinc-700 dark:text-zinc-300">Enter your email to receive a reset link.</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={onSubmit} className="space-y-4">
            {msg && (
              <InlineBanner variant="success" className="text-center">
                <div className="space-y-1">
                  <p className="font-medium">Check your email</p>
                  <p className="text-sm">{msg}</p>
                </div>
              </InlineBanner>
            )}
            {err && (
              <InlineBanner variant="error" className="text-center">{err}</InlineBanner>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11 bg-white dark:bg-zinc-800 border-lime-400 dark:border-lime-500" />
            </div>
            <Button type="submit" disabled={loading} variant="default" className="w-full h-11 rounded-lg">
              {loading ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Sending.</span>
              ) : (
                "Send reset link"
              )}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="py-4 px-6">
          <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:justify-between">
            <Button asChild variant="secondary" size="sm" className="h-9 px-3 w-full sm:w-auto">
              <Link href="/login">Back to sign in</Link>
            </Button>
            <Button asChild variant="secondary" size="sm" className="h-9 px-3 w-full sm:w-auto">
              <Link href="/register">Create account</Link>
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
